import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import {
  Groups,
  GroupRemove,
  TableView,
  ChevronRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  themes,
  scrollTop,
  tableRowSize,
  stickyHeader,
} from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { checkViewerPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import {
  operationsDailyPayrunHeader,
  DailyPayrunAmountDateView,
  expandHearder,
} from "pages/PayRoll/PayRollHeaders";
import { tableRowSx } from "services/constants";
import { Box, Paper } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { toFlatDisplayHeaders } from "components/CustomComponents/headerGroupUtils";
export const DateView = ({ startDate, endDate, subModuleId }) => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  const [rowExpandData, setRowExpandData] = useState([]);
  const [rowExpandId, setRowExpandId] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dailyPayRunDetails, setDailyPayrunDetails] = useState([]);
  const [dailyPayRunData, setDailyPayRunData] = useState([]);
  const containerRef = useRef(null);
  let check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Payroll Processing",
    "Daily Payrun",
  );

  useEffect(() => {
    const getPaySchedule = async () => {
      if (startDate && endDate) {
        setLoading(true);
        const res = await customGetAPI(
          `${domain}payroll/daily_payroll_summary_amount/?company_id=${topbarCompanyId}&start_date=${startDate}&end_date=${endDate}`,
        );
        if (res.ok) {
          setDailyPayRunData(res?.responseDetails);
          setLoading(false);
        }
      }
    };
    getPaySchedule();
  }, [domain, endDate, startDate, topbarCompanyId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);
  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setDailyPayrunDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const round2 = (num) => Number(num.toFixed(2));

  const sumByKey = (data, key) =>
    round2(
      data?.reduce((sum, item) => {
        return sum + (Number(item?.[key]) || 0);
      }, 0) ?? 0,
    );

  const totalOvertime = sumByKey(dailyPayRunDetails, "ot_amount");
  const totalEarnings = sumByKey(dailyPayRunDetails, "earning_amount");
  const totalAmount = sumByKey(dailyPayRunDetails, "total_amount");

  const payrollData = [
    {
      id: 1,
      icon: TableView,
      label: "Regular Pay",
      value: totalEarnings,
      bgColor: "#F0FDFA",
      iconBgColor: "#D1FAF5",
      iconBorderColor: "#0F766E",
      iconColor: "#09BD71",
    },
    {
      id: 2,
      icon: GroupRemove,
      label: "OT Pay",
      value: totalOvertime,
      bgColor: "#FFF1F2",
      iconBgColor: "#FFE4E6",
      iconBorderColor: "#DC2626",
      iconColor: "#DC2626",
    },
    {
      id: 3,
      icon: Groups,
      label: "Total",
      value: totalAmount,
      bgColor: "#F5F7FF",
      iconBgColor: "#E0E7FF",
      iconBorderColor: "#1D4ED8",
      iconColor: "#1D4ED8",
    },
  ];

  const rowExpandDetails = async (id) => {
    if (id) {
      const res = await customGetAPI(
        `${domain}payroll/daily_cycle_amount/?summary_table_id=${id}&company_id=${topbarCompanyId}`,
      );
      if (res.ok) {
        setRowExpandData(res.responseDetails);
        setRowExpandId(id);
      }
    }
  };

  const activeHeaders = useMemo(
    () => DailyPayrunAmountDateView.filter((header) => header.key !== ""),
    [],
  );

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "daywise_payrun_saved_views",
    fullHeaders: activeHeaders,
    screenAccess: check_access,
    onViewChange: () => setPage(0),
  });

  // Same merge as DailyPayRun/index.js: keep the filter metadata
  // (type/icon/date-range) from operationsDailyPayrunHeader while letting the
  // saved view drive column selection/order/rename.
  const richHeadersByKey = useMemo(() => {
    const map = new Map();
    operationsDailyPayrunHeader.forEach((header) => map.set(header.key, header));
    return map;
  }, []);

  const exportFilterHeaders = useMemo(
    () =>
      toFlatDisplayHeaders(visibleHeaders).map((header) => ({
        ...richHeadersByKey.get(header.key),
        ...header,
      })),
    [visibleHeaders, richHeadersByKey],
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Paper
          sx={{
            width: "100%",
            height: "78vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden", // important
          }}
        >
          <CustomFilter
            title="Daywise Pay Run Records"
            titleOverride={titleOverride}
            filterHeaders={exportFilterHeaders}
            data={dailyPayRunData}
            filteredData={dailyPayRunDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={newButtonComponent}
          />
          <Box>
            <Mui.Box sx={{ flexGrow: 1, overflow: "auto", pt: 0, mb: 2 }}>
              <Mui.Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 4,
                  paddingLeft: 3,
                }}
              >
                {payrollData.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Mui.Card
                      key={item.id}
                      sx={{
                        width: 250,
                        height: 123,
                        bgcolor: themes?.whiteColor,
                        borderRadius: 1,
                        boxShadow: "0px 4px 4px rgba(0,0,0,0.25)",
                        border: `0.5px solid ${item.iconBorderColor}`,
                      }}
                    >
                      <Mui.CardContent
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        <Mui.Box
                          sx={{
                            width: 35,
                            height: 35,
                            bgcolor: item.iconBgColor,
                            border: `0.5px solid ${item.iconBorderColor}`,
                            borderRadius: 1,
                            boxShadow: "0px 1px 2px rgba(0,0,0,0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                          }}
                        >
                          <IconComponent
                            width={16}
                            height={16}
                            style={{ color: item.iconColor, padding: 2 }}
                          />
                        </Mui.Box>
                        <Mui.Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: "auto",
                          }}
                        >
                          {item.label}
                        </Mui.Typography>
                        <Mui.Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#32325D",
                          }}
                        >
                          {item.value}
                        </Mui.Typography>
                      </Mui.CardContent>
                    </Mui.Card>
                  );
                })}
              </Mui.Box>
            </Mui.Box>
          </Box>
          <Mui.Grid
            sx={{
              ml: 2,
              mr: 2,
              flex: 1, // ⭐ take remaining height
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Mui.TableContainer
              ref={containerRef}
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    <Mui.TableCell
                      align="center"
                      rowSpan={2}
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        border: "1px solid #ddd",
                      }}
                    />
                    {visibleHeaders.map((header) => (
                      <Mui.TableCell
                        key={header.name}
                        align="center"
                        colSpan={header.children ? header.children.length : 1}
                        rowSpan={header.children ? 1 : 2}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                          border: "1px solid #ddd",
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                  <Mui.TableRow sx={tableRowSize}>
                    {visibleHeaders.filter(
                      (header) => header.children,
                    ).flatMap((header) =>
                      header.children.map((child) => (
                        <Mui.TableCell
                          key={child.key}
                          align="center"
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                            border: "1px solid #ddd",
                          }}
                        >
                          {child.name}
                        </Mui.TableCell>
                      )),
                    )}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {dailyPayRunDetails?.length === 0 ? (
                    <TableEmptyState dataLength={dailyPayRunData} />
                  ) : (
                    dailyPayRunDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((row) => (
                        <React.Fragment key={row.department_id}>
                          <Mui.TableRow sx={tableRowSize}>
                            <Mui.TableCell align="center" sx={{ border: "1px solid #ddd" }}>
                              {rowExpandId === row.id ? (
                                <KeyboardArrowDown
                                  size="medium"
                                  onClick={() => {
                                    if (rowExpandId !== row.id) {
                                      rowExpandDetails(row.id);
                                    } else {
                                      setRowExpandId(null);
                                    }
                                  }}
                                />
                              ) : (
                                <ChevronRight
                                  size="medium"
                                  onClick={() => {
                                    if (rowExpandId !== row.id) {
                                      rowExpandDetails(row.id);
                                    } else {
                                      setRowExpandId(null);
                                    }
                                  }}
                                />
                              )}
                            </Mui.TableCell>
                            {visibleHeaders.flatMap((header) =>
                              header.children
                                ? header.children.map((child) => (
                                    <Mui.TableCell
                                      key={child.key}
                                      align="center"
                                      sx={{ border: "1px solid #ddd" }}
                                    >
                                      {row[child.key] ?? "-"}
                                    </Mui.TableCell>
                                  ))
                                : [
                                    <Mui.TableCell
                                      key={header.key}
                                      align="center"
                                      sx={{ border: "1px solid #ddd" }}
                                    >
                                      {row[header.key] ?? "-"}
                                    </Mui.TableCell>,
                                  ],
                            )}
                          </Mui.TableRow>
                          {row.id === rowExpandId && (
                            <Mui.TableRow sx={tableRowSx}>
                              <Mui.TableCell colSpan={12}>
                                <Mui.TableContainer
                                  sx={{
                                    marginTop: 0,
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    width: "100%",
                                  }}
                                  className="border rounded"
                                >
                                  <Mui.Table className="border">
                                    <Mui.TableHead
                                      sx={{
                                        backgroundColor: themes.primary,
                                        position: "sticky",
                                        top: -1,
                                        zIndex: 10,
                                      }}
                                    >
                                      <Mui.TableRow sx={tableRowSize}>
                                        {expandHearder.map((header) => (
                                          <Mui.TableCell
                                            key={header.name}
                                            align="center"
                                            colSpan={
                                              header.children
                                                ? header.children.length
                                                : 1
                                            }
                                            rowSpan={header.children ? 1 : 2}
                                            sx={{
                                              color: themes.headLine,
                                              fontWeight: "bold",
                                              border: "1px solid #ddd",
                                              height: 50,
                                            }}
                                          >
                                            {header.name}
                                          </Mui.TableCell>
                                        ))}
                                      </Mui.TableRow>
                                      <Mui.TableRow sx={tableRowSize}>
                                        {expandHearder
                                          .filter((header) => header.children)
                                          .flatMap((header) =>
                                            header.children.map((child) => (
                                              <Mui.TableCell
                                                key={child.key}
                                                align="center"
                                                sx={{
                                                  color: themes.headLine,
                                                  fontWeight: "bold",
                                                  border: "1px solid #ddd",
                                                }}
                                              >
                                                {child.name}
                                              </Mui.TableCell>
                                            )),
                                          )}
                                      </Mui.TableRow>
                                    </Mui.TableHead>
                                    <Mui.TableBody>
                                      {rowExpandData.length > 0 ? (
                                        rowExpandData.map((shift, index) => (
                                          <Mui.TableRow key={index}>
                                            {expandHearder.flatMap((header) =>
                                              header.children
                                                ? header.children.map(
                                                    (child) => (
                                                      <Mui.TableCell
                                                        key={child.key}
                                                        align="center"
                                                        sx={{
                                                          border:
                                                            "1px solid #ddd",
                                                        }}
                                                      >
                                                        {shift[child.key] ??
                                                          "-"}
                                                      </Mui.TableCell>
                                                    ),
                                                  )
                                                : [
                                                    <Mui.TableCell
                                                      key={header.key}
                                                      align="center"
                                                      sx={{
                                                        border:
                                                          "1px solid #ddd",
                                                      }}
                                                    >
                                                      {shift[header.key] ?? "-"}
                                                    </Mui.TableCell>,
                                                  ],
                                            )}
                                          </Mui.TableRow>
                                        ))
                                      ) : (
                                        <Mui.TableRow sx={tableRowSx}>
                                          <Mui.TableCell
                                            colSpan={12}
                                            sx={{ padding: 0.5 }}
                                          >
                                            <Mui.Alert severity="info">
                                              No Records Found
                                            </Mui.Alert>
                                          </Mui.TableCell>
                                        </Mui.TableRow>
                                      )}
                                    </Mui.TableBody>
                                  </Mui.Table>
                                </Mui.TableContainer>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {dailyPayRunDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={dailyPayRunDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>
    </React.Fragment>
  );
};
